import type { Remote } from 'comlink'
import type { SdkHandlersProvider, InvokeNamespaceHandler, PlatformEnvDataProvider } from '@wix/thunderbolt-symbols'
import { SiteFeatureConfigSymbol } from '@wix/thunderbolt-symbols'
import { named, withDependencies } from '@wix/thunderbolt-ioc'
import { name } from './symbols'
import type { ClientSdk, ClientSdkSiteConfig } from './types'

type ClientSdkHandlers = {
	[name]: {
		onPlatformReady: (invokeNamespaceHandler: Remote<InvokeNamespaceHandler>) => void
		invokeProxyFunction: (id: string, args: Array<any>) => unknown
	}
}

export const ClientSdkHandlersProvider = withDependencies(
	[named(SiteFeatureConfigSymbol, name)],
	(siteFeatureConfig?: ClientSdkSiteConfig): SdkHandlersProvider<ClientSdkHandlers> & PlatformEnvDataProvider => {
		const fnProxyMap: Record<string, Function> = {}

		const serializeArguments = (args: Array<any>) => {
			return args.map((arg) => {
				if (typeof arg === 'function') {
					const id = Math.random().toString(36).substring(7)
					fnProxyMap[id] = arg
					return { __client_sdk_type__: 'function', id }
				}

				return arg
			})
		}

		return {
			getSdkHandlers: () => {
				return {
					[name]: {
						onPlatformReady: async (invokeNamespaceHandler) => {
							const clientSdk: ClientSdk = {
								invoke: async ({ applicationId, namespace, method, args = [] }) => {
									if (!applicationId) {
										throw new Error('Missing required property "applicationId"')
									}

									if (!namespace) {
										throw new Error('Missing required property "namespace"')
									}

									if (!method) {
										throw new Error('Missing required property "method"')
									}

									return invokeNamespaceHandler({
										applicationId,
										namespace,
										method,
										args: serializeArguments(args),
									})
								},
							}

							window.__clientSdk__ = clientSdk
							window.resolveExternalsRegistryModule('clientSdk')
						},
						invokeProxyFunction: async (id, args) => {
							const fn = fnProxyMap[id]
							return fn(...args)
						},
					},
				}
			},
			platformEnvData() {
				return {
					[name]: {
						shouldLoad: true,
						appDefinitionIds: siteFeatureConfig?.appDefinitionIds || [],
					},
				}
			},
		}
	}
)
